import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppWindow, LifeBuoy } from 'lucide-react';
import { Modal } from '../Modal';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import type { ToastType } from '../Toast';
import { socketService } from '../../services/socketService';
import {
  PcSummary,
  RemoteSupportTarget,
  RsSignal,
  listenNative,
  onRemoteSupportRequest,
  remoteSupportApi,
  rsInvoke,
  useRemoteSupportMe,
} from '../../services/remoteSupportService';
import {
  MainToViewer,
  ViewerCommand,
  ViewerControl,
  ViewerLink,
  ViewerSnapshot,
  ViewerSummary,
  ViewerToMain,
  ViewerWindowHandle,
  closeStaleViewerWindow,
  createViewerNonce,
  openMainLink,
  openViewerWindow,
} from '../../services/remoteViewerLink';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { flashTaskbar, isTauri } from '../../utils/tauri';
import { showWebNotification } from '../../utils/webNotification';
import { handleAvatarError, User } from '../../types';
import { endReasonLabel, filesLabel, formatDuration } from '../../utils/remoteSupportLabels';

/**
 * 원격지원 — 담당자(헬프데스크) 쪽. 웹·PC 앱 어디서든 쓴다(화면을 받기만 하므로 Rust가 필요 없다).
 *
 * 원격 화면은 채팅 앱 안이 아니라 **원격 전용 창**(RemoteViewerWindow)에 뜬다. 이 컴포넌트는 그 창의 중개자다:
 *  - 소켓 구독·세션 상태 머신은 여기 있다(서버는 세션을 담당자 socketId에 고정한다 — 창이 소켓을 따로 열 수 없다)
 *  - 상태가 바뀔 때마다 스냅샷을 창에 보내고, 창의 명령·시그널·폴백 입력을 소켓으로 옮긴다(remoteViewerLink.ts)
 *  - 채팅 앱에는 작은 상태 카드([창 보기] [종료])와 직원 요청 수락 카드만 남는다
 *
 * 흐름: 프로필 '원격지원 시작' → (창 열기) rs:request → (직원 허용) rs:accepted → (화면 선택) rs:started →
 *       창의 RsPeer가 offer/answer → '제어 요청' → (직원 PC 네이티브 동의창) rs:control:granted → 입력
 *
 * 직원이 먼저 요청한 경우: rs:ask:incoming → 수락 카드(우상단) → [수락] acceptAsk() → (창 열기) rs:ask:accept →
 *       'waiting'(직원이 화면 공유를 누르기를 기다림) → 이후 위와 같다. rs:ask:handled는 카드를 닫는다
 *       (다른 기기에서 수락·직원 취소·만료).
 *
 * ⚠ 창은 클릭 핸들러 안에서 **await 전에** 연다(startViewer) — 웹은 사용자 제스처가 끝나면 팝업이 차단된다.
 */

type Phase = 'idle' | 'opening' | 'waiting' | 'picking' | 'viewing';

/** 직원이 나에게 보낸 원격지원 요청(수락 전) */
interface AskCard {
  sessionId: string;
  requester: { name: string; avatar: string | null; depart: string | null };
  expiresAt: number;
}

const ASK_HANDLED_TOAST: Record<string, (name: string) => string> = {
  canceled: (n) => `${n}님이 원격지원 요청을 취소했습니다.`,
  timeout: (n) => `${n}님의 원격지원 요청에 응답하지 않아 요청이 만료되었습니다.`,
  disconnect: (n) => `${n}님의 연결이 끊겨 원격지원 요청이 취소되었습니다.`,
  policy_off: () => '관리자가 원격지원을 꺼 요청이 취소되었습니다.',
  operator_revoked: () => '원격지원 담당자 권한이 해제되어 요청이 취소되었습니다.',
};

const ENDED_TOAST: Record<string, string> = {
  declined: '직원이 원격지원 요청을 거절했습니다.',
  timeout: '직원이 응답하지 않아 요청이 만료되었습니다.',
  picker_canceled: '직원이 화면 공유를 취소해 시작하지 못했습니다.',
  policy_off: '관리자가 원격지원을 꺼 세션이 끝났습니다.',
  operator_revoked: '원격지원 담당자 권한이 해제되어 세션이 끝났습니다.',
};

const CONTROL_DENIED_TOAST: Record<string, string> = {
  declined: '직원이 제어 요청을 거절했습니다.',
  timeout: '직원이 응답하지 않아 제어 요청이 만료되었습니다.',
};

const CONTROL_REVOKED_TOAST: Record<string, string> = {
  host_revoked: '직원이 제어를 되찾았습니다. 화면은 계속 볼 수 있습니다.',
  expired: '제어 시간(30분)이 끝났습니다. 계속하려면 다시 요청해 주세요.',
  idle: '입력이 한동안 없어 제어가 끝났습니다. 계속하려면 다시 요청해 주세요.',
  display_changed: '직원 PC의 화면 구성이 바뀌어 제어가 끝났습니다. 다시 요청해 주세요.',
  policy_off: '관리자가 원격 제어를 막았습니다. 화면 보기만 할 수 있습니다.',
  gate_closed: '제어가 끝났습니다. 계속하려면 다시 요청해 주세요.',
};

const DISCONNECTED_MESSAGE = '연결이 끊겨 원격지원이 끝났습니다.';
const POPUP_BLOCKED_TOAST = '원격지원 창을 열지 못했습니다. 브라우저에서 이 사이트의 팝업을 허용한 뒤 다시 시도해 주세요.';
const VIEWER_FAILED_TOAST = '원격지원 창을 열지 못했습니다. 잠시 후 다시 시도해 주세요.';
const BUSY_TOAST = '이미 진행 중인 원격지원이 있습니다. 먼저 종료해 주세요.';

/** 창이 떠서 hello를 보내기까지 기다리는 시간 */
const HELLO_TIMEOUT_MS = 15000;
/** PC앱 ×를 창에 넘긴 뒤 응답(확인창 표시)을 기다리는 시간 — 넘기면 창이 멈춘 것으로 본다 */
const CLOSE_ACK_TIMEOUT_MS = 2000;
const MAX_RELAY_EVENTS = 200;
const NOTE_MAX = 1000;

interface Props {
  /** '대화 열기' — 직원과의 1:1 대화방(초안 DM 포함) */
  onOpenDm: (user: User) => void;
}

interface EndView {
  summary: ViewerSummary | null;
  message: string | null;
}

export const RemoteSupportOperator: React.FC<Props> = ({ onOpenDm }) => {
  const { showToast } = useToast();
  const [phase, setPhase] = useState<Phase>('idle');
  const [target, setTarget] = useState<RemoteSupportTarget | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [controlEnabled, setControlEnabled] = useState(false);
  /** 파일 주고받기 정책 — 창 스냅샷에 싣는다(전송 자체는 창의 P2P 'fx'가 한다) */
  const fileTransferEnabled = useRemoteSupportMe().fileTransferEnabled;
  const [control, setControl] = useState<ViewerControl>('none');
  const [pcInfo, setPcInfo] = useState<PcSummary | null>(null);
  const [secureDesktop, setSecureDesktop] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [confirmEnd, setConfirmEnd] = useState(false);
  /** 창에 보여 줄 끝 화면 — 세션은 끝났지만 창은 요약·메모를 위해 남아 있다 */
  const [endView, setEndView] = useState<EndView | null>(null);
  /** 창이 없을 때(닫혔거나 멈춤)의 요약 — 채팅 앱에 띄운다 */
  const [summary, setSummary] = useState<ViewerSummary | null>(null);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [askCard, setAskCard] = useState<AskCard | null>(null);
  /** 지금 세션이 직원의 요청으로 시작됐는가 — 대기 문구만 바뀐다 */
  const [askedFlow, setAskedFlow] = useState(false);

  const sessionRef = useRef<string | null>(null);
  const targetRef = useRef<RemoteSupportTarget | null>(null);
  const askRef = useRef<AskCard | null>(null);
  const phaseRef = useRef<Phase>('idle');
  phaseRef.current = phase;
  const controlRef = useRef<ViewerControl>('none');
  controlRef.current = control;
  const endViewRef = useRef<EndView | null>(null);
  endViewRef.current = endView;
  /** 요청 ack를 기다리는 동안 취소했다 — ack가 오면 바로 끝낸다 */
  const cancelPendingRef = useRef(false);

  // ── 뷰어 창 ──
  const linkRef = useRef<ViewerLink<MainToViewer> | null>(null);
  const handleRef = useRef<ViewerWindowHandle | null>(null);
  /** hello를 받았다 */
  const viewerReadyRef = useRef(false);
  /** 시그널을 한 번이라도 창에 넘겼다 — 그 뒤의 hello는 '창이 새로 고쳐져 피어를 잃었다'는 뜻이다 */
  const signalsDeliveredRef = useRef(false);
  /** hello 전에 온 시그널(직원 offer 등) */
  const pendingSignalsRef = useRef<RsSignal[]>([]);
  const helloTimerRef = useRef<number | null>(null);
  const closeAckTimerRef = useRef<number | null>(null);

  // 경과 시간·카운트다운 — 세션 중이거나 수락 카드가 떠 있을 때
  const hasAskCard = !!askCard;
  useEffect(() => {
    if (phase === 'idle' && !hasAskCard) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [phase, hasAskCard]);

  // ── 창에 보내는 스냅샷 ──

  const snap: ViewerSnapshot = {
    phase: phase === 'idle' ? 'ended' : phase,
    asked: askedFlow,
    target,
    code,
    controlEnabled,
    control,
    pcInfo,
    secureDesktop,
    expiresAt,
    startedAt,
    summary: endView?.summary ?? null,
    endedMessage: endView?.message ?? null,
    fileTransferEnabled,
  };
  const snapRef = useRef(snap);
  snapRef.current = snap;

  useEffect(() => {
    if (viewerReadyRef.current) linkRef.current?.post({ type: 'state', snap: snapRef.current });
  }, [phase, askedFlow, target, code, controlEnabled, control, pcInfo, secureDesktop, expiresAt, startedAt, endView, fileTransferEnabled]);

  const clearTimer = (r: React.MutableRefObject<number | null>) => {
    if (r.current) {
      window.clearTimeout(r.current);
      r.current = null;
    }
  };

  /** 창과의 링크를 놓는다(창 자체를 닫는 것은 closeViewer) */
  const dropViewer = useCallback(() => {
    clearTimer(helloTimerRef);
    clearTimer(closeAckTimerRef);
    linkRef.current?.close();
    linkRef.current = null;
    handleRef.current = null;
    viewerReadyRef.current = false;
    signalsDeliveredRef.current = false;
    pendingSignalsRef.current = [];
  }, []);

  const closeViewer = useCallback(() => {
    const h = handleRef.current;
    dropViewer();
    h?.close();
  }, [dropViewer]);

  /** 세션 알림 — 창이 떠 있으면 창에(담당자가 보고 있는 곳), 아니면 채팅 앱에 */
  const notify = useCallback(
    (type: ToastType, message: string) => {
      if (viewerReadyRef.current && linkRef.current) linkRef.current.post({ type: 'toast', toastType: type, message });
      else showToast({ type, message });
    },
    [showToast]
  );

  const resetSession = useCallback(() => {
    sessionRef.current = null;
    cancelPendingRef.current = false;
    pendingSignalsRef.current = [];
    phaseRef.current = 'idle';
    setPhase('idle');
    setControl('none');
    setPcInfo(null);
    setSecureDesktop(false);
    setStartedAt(null);
    setExpiresAt(null);
    setConfirmEnd(false);
    setAskedFlow(false);
  }, []);

  /** 세션이 끝났다 — 창이 있으면 그 창에 끝 화면을, 없으면 채팅 앱에 요약·토스트를 */
  const finish = useCallback(
    (end: { summary?: ViewerSummary; message?: string }) => {
      resetSession();
      if (linkRef.current) {
        // hello 전이어도 창이 뜨는 중이면 거기에 보여 준다(hello 때 스냅샷이 간다)
        if (!end.summary && !end.message) closeViewer(); // 담당자가 취소했다 — 보여 줄 것이 없다
        else setEndView({ summary: end.summary ?? null, message: end.message ?? null });
      } else if (end.summary) {
        setNote('');
        setSummary(end.summary);
      } else if (end.message) {
        showToast({ type: 'info', message: end.message });
      }
    },
    [closeViewer, resetSession, showToast]
  );
  const finishRef = useRef(finish);
  finishRef.current = finish;

  /** 진행 중인 세션(또는 ack를 기다리는 요청)을 끝낸다 */
  const endSession = useCallback(() => {
    const sessionId = sessionRef.current;
    if (!sessionId) {
      if (phaseRef.current === 'opening') cancelPendingRef.current = true;
      return;
    }
    socketService.emitRemoteSupport('rs:end', { sessionId });
    // 서버 rs:ended가 끝 화면을 띄운다. 연결이 없으면 오지 않으므로 그때는 바로 정리한다
    if (!socketService.isConnected()) finishRef.current({ message: DISCONNECTED_MESSAGE });
  }, []);

  /** 창이 사라졌다(닫힘·새로고침·멈춰서 걷음) — 진행 중이면 세션을 끝내고 요약은 채팅 앱에 */
  const onViewerGone = useCallback(() => {
    if (!linkRef.current) return;
    dropViewer();
    setEndView(null);
    endSession();
  }, [dropViewer, endSession]);

  const onViewerFailed = useCallback(() => {
    if (!linkRef.current || viewerReadyRef.current) return;
    closeViewer();
    endSession();
    showToast({ type: 'error', message: VIEWER_FAILED_TOAST });
  }, [closeViewer, endSession, showToast]);
  const onViewerFailedRef = useRef(onViewerFailed);
  onViewerFailedRef.current = onViewerFailed;
  const onViewerGoneRef = useRef(onViewerGone);
  onViewerGoneRef.current = onViewerGone;

  // ── 창 명령 ──

  const requestControl = async () => {
    const sessionId = sessionRef.current;
    if (!sessionId || controlRef.current !== 'none') return;
    setControl('requested');
    const ack = await socketService.emitRemoteSupportWithAck('rs:control:request', { sessionId });
    if (!ack.ok) {
      setControl('none');
      notify('error', (ack as { message?: string }).message || '제어를 요청하지 못했습니다.');
      return;
    }
    notify('info', '직원 PC에 제어 동의 창을 띄웠습니다. 직원이 허용하면 시작됩니다.');
  };

  const returnControl = () => {
    const sessionId = sessionRef.current;
    if (!sessionId) return;
    setControl('none');
    socketService.emitRemoteSupport('rs:control:revoke', { sessionId, reason: 'operator_returned' });
  };

  const openDm = () => {
    const t = targetRef.current;
    if (!t?.userId) return;
    onOpenDm({ id: t.userId, username: t.name, avatar: t.avatar || undefined, depart: t.depart || undefined } as User);
    // PC앱: 채팅 창을 앞으로(트레이에 숨어 있어도). 웹은 창이 클릭 안에서 opener.focus()를 부른다
    if (isTauri()) rsInvoke('rs_bring_to_front').catch(() => {});
  };

  const saveNoteFromViewer = async (text: string) => {
    const c = endViewRef.current?.summary?.code;
    if (!c) {
      linkRef.current?.post({ type: 'note-result', ok: false, message: '저장할 원격지원 기록이 없습니다.' });
      return;
    }
    try {
      await remoteSupportApi.saveNote(c, text.slice(0, NOTE_MAX));
      linkRef.current?.post({ type: 'note-result', ok: true });
      // 창은 곧 닫힌다 — 안내는 채팅 앱에
      showToast({ type: 'success', message: '처리 메모를 저장했습니다.' });
    } catch (err: any) {
      linkRef.current?.post({
        type: 'note-result',
        ok: false,
        message: err?.message || '메모를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.',
      });
    }
  };

  const onCommand = (c: ViewerCommand) => {
    switch (c.name) {
      case 'request-control':
        void requestControl();
        return;
      case 'return-control':
        returnControl();
        return;
      case 'end':
        endSession();
        return;
      case 'open-dm':
        openDm();
        return;
      case 'save-note':
        void saveNoteFromViewer(c.note);
        return;
      default:
    }
  };

  const onViewerMessage = (m: ViewerToMain) => {
    const link = linkRef.current;
    if (!link) return;
    switch (m.type) {
      case 'hello': {
        if (viewerReadyRef.current && signalsDeliveredRef.current) {
          // 창이 새로 고쳐졌다 — 피어를 잃었다(재협상은 지원하지 않는다). 끝 화면은 새 창에 뜬다
          signalsDeliveredRef.current = false;
          pendingSignalsRef.current = [];
          endSession();
        }
        viewerReadyRef.current = true;
        clearTimer(helloTimerRef);
        link.post({ type: 'state', snap: snapRef.current });
        for (const data of pendingSignalsRef.current.splice(0)) {
          link.post({ type: 'signal', data });
          signalsDeliveredRef.current = true;
        }
        return;
      }
      case 'signal':
        if (sessionRef.current) socketService.emitRemoteSupport('rs:signal', { sessionId: sessionRef.current, data: m.data });
        return;
      case 'input':
        // DataChannel 폴백 — 서버도 매번 controlling을 검사한다
        if (sessionRef.current && controlRef.current === 'granted' && Array.isArray(m.events)) {
          socketService.emitRemoteSupport('rs:input', {
            sessionId: sessionRef.current,
            events: m.events.slice(0, MAX_RELAY_EVENTS),
          });
        }
        return;
      case 'cmd':
        onCommand(m.cmd);
        return;
      case 'close-ack':
        clearTimer(closeAckTimerRef);
        return;
      case 'ping':
        link.post({ type: 'pong' });
        return;
      case 'closing':
        onViewerGone();
        return;
      default:
    }
  };
  const onViewerMessageRef = useRef(onViewerMessage);
  onViewerMessageRef.current = onViewerMessage;

  /**
   * 창을 연다. ⚠ 사용자 제스처(클릭) 안에서 **await 전에 동기로** 부를 것 — 웹은 팝업 차단에 걸린다.
   * 지난 세션의 창(요약 화면)이 남아 있으면 그 창이 새 세션 주소로 바뀐다.
   */
  const startViewer = useCallback(
    (name: string): boolean => {
      dropViewer();
      setEndView(null);
      const nonce = createViewerNonce();
      const handle = openViewerWindow(nonce, `${name}님 PC · 원격지원`, () => onViewerFailedRef.current());
      if (!handle) {
        showToast({ type: 'error', message: POPUP_BLOCKED_TOAST });
        return false;
      }
      handleRef.current = handle;
      linkRef.current = openMainLink(nonce, (m) => onViewerMessageRef.current(m));
      helloTimerRef.current = window.setTimeout(() => {
        helloTimerRef.current = null;
        onViewerFailedRef.current();
      }, HELLO_TIMEOUT_MS);
      return true;
    },
    [dropViewer, showToast]
  );

  /** 새 세션 준비(창을 연 직후, ack 전) */
  const beginOpening = (t: RemoteSupportTarget, asked: boolean) => {
    targetRef.current = t;
    setTarget(t);
    setCode(null);
    setSummary(null);
    setControl('none');
    setStartedAt(null);
    setExpiresAt(null);
    setAskedFlow(asked);
    cancelPendingRef.current = false;
    phaseRef.current = 'opening';
    setPhase('opening');
  };

  /** ack를 받아 대기 단계로 */
  const beginWaiting = (sessionId: string, expiresInMs: number | undefined) => {
    sessionRef.current = sessionId;
    setNow(Date.now());
    setExpiresAt(Date.now() + (expiresInMs || 60000));
    phaseRef.current = 'waiting';
    setPhase('waiting');
    if (cancelPendingRef.current) {
      cancelPendingRef.current = false;
      socketService.emitRemoteSupport('rs:end', { sessionId });
    }
  };

  /** PC앱 창의 × — 보는 중이면 창에 확인창을, 그 밖의 단계는 바로 닫는다 */
  const onCloseRequested = () => {
    const link = linkRef.current;
    if (!link || !viewerReadyRef.current || phaseRef.current !== 'viewing') {
      // 대기·선택 단계는 요청 취소, 끝 화면·고아 창은 그냥 닫기
      if (phaseRef.current !== 'idle') endSession();
      closeViewer();
      return;
    }
    link.post({ type: 'close-requested' });
    clearTimer(closeAckTimerRef);
    closeAckTimerRef.current = window.setTimeout(() => {
      closeAckTimerRef.current = null;
      // 창이 응답하지 않는다(멈춤) — 세션을 끝내고 창을 걷는다. 요약은 채팅 앱에 뜬다
      closeViewer();
      endSession();
    }, CLOSE_ACK_TIMEOUT_MS);
  };
  const onCloseRequestedRef = useRef(onCloseRequested);
  onCloseRequestedRef.current = onCloseRequested;

  const closeAskCard = useCallback(() => {
    askRef.current = null;
    setAskCard(null);
  }, []);

  // ── 요청 ──

  useEffect(() => {
    return onRemoteSupportRequest(async (t) => {
      if (phaseRef.current !== 'idle') {
        showToast({ type: 'info', message: BUSY_TOAST });
        handleRef.current?.focus();
        return;
      }
      // await 전에 — 클릭의 사용자 활성화가 살아 있을 때 창을 연다
      if (!startViewer(t.name)) return;
      beginOpening(t, false);
      const ack = await socketService.emitRemoteSupportWithAck<{
        ok: boolean;
        sessionId?: string;
        hostName?: string;
        expiresInMs?: number;
        message?: string;
      }>('rs:request', { targetUserId: t.userId, targetAffiliateId: t.affiliateId ?? undefined });
      if ((phaseRef.current as Phase) !== 'opening') return; // 그사이 정리됐다(로그아웃 등)
      if (!ack.ok || !ack.sessionId) {
        finish({ message: ack.message || '원격지원을 요청하지 못했습니다.' });
        return;
      }
      targetRef.current = { ...t, name: ack.hostName || t.name };
      setTarget(targetRef.current);
      beginWaiting(ack.sessionId, ack.expiresInMs);
    });
  }, [finish, showToast, startViewer]);

  // ── 직원이 먼저 요청: 수락 카드 ──

  /**
   * [수락] — 첫 줄부터 await 전까지는 클릭 핸들러와 같은 동기 구간이다(사용자 제스처가 살아 있다).
   * 창(startViewer)은 이 구간에서 연다. 팝업이 막히면 카드를 그대로 두어 허용 후 다시 누를 수 있게 한다.
   */
  const acceptAsk = useCallback(async () => {
    const card = askRef.current;
    if (!card) return;
    if (phaseRef.current !== 'idle') {
      closeAskCard();
      showToast({ type: 'info', message: BUSY_TOAST });
      socketService.emitRemoteSupport('rs:ask:decline', { sessionId: card.sessionId });
      return;
    }
    if (!startViewer(card.requester.name)) return;
    closeAskCard();
    void flashTaskbar(true);
    // 사번은 ack가 알려 준다 — 그 전에는 창 머리에 이름·사진만 보인다
    beginOpening({ userId: '', name: card.requester.name, avatar: card.requester.avatar, depart: card.requester.depart }, true);
    const ack = await socketService.emitRemoteSupportWithAck<{
      ok: boolean;
      sessionId?: string;
      hostName?: string;
      expiresInMs?: number;
      host?: { userId: string; affiliateId: string | null; name: string };
      message?: string;
    }>('rs:ask:accept', { sessionId: card.sessionId });
    if ((phaseRef.current as Phase) !== 'opening') return;
    if (!ack.ok || !ack.sessionId || !ack.host) {
      finish({ message: ack.message || '요청을 수락하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      return;
    }
    targetRef.current = {
      userId: ack.host.userId,
      name: ack.hostName || ack.host.name || card.requester.name,
      avatar: card.requester.avatar,
      depart: card.requester.depart,
      affiliateId: ack.host.affiliateId,
    };
    setTarget(targetRef.current);
    beginWaiting(ack.sessionId, ack.expiresInMs);
  }, [closeAskCard, finish, showToast, startViewer]);

  const declineAsk = useCallback(() => {
    const card = askRef.current;
    if (!card) return;
    closeAskCard();
    void flashTaskbar(true);
    socketService.emitRemoteSupport('rs:ask:decline', { sessionId: card.sessionId });
  }, [closeAskCard]);

  /** 다른 창을 보고 있어도 알아차리게 — PC 앱은 토스트·작업표시줄 점멸, 웹은 탭이 숨어 있을 때 시스템 알림 */
  const notifyAsk = useCallback((card: AskCard) => {
    const title = '원격지원 요청';
    const body = `${card.requester.name}님이 원격지원을 요청했습니다.`;
    if (isTauri()) {
      // channelId 자리의 'remote-support'는 App의 notification-clicked가 채널 이동 없이 무시한다(창은 Rust가 연다)
      import('@tauri-apps/api/core')
        .then(({ invoke }) => invoke('show_chat_notification', { title, body, channelId: 'remote-support' }))
        .catch(() => {});
      void flashTaskbar(false);
    } else if (document.visibilityState !== 'visible') {
      void showWebNotification(title, {
        body,
        tag: `rs-ask-${card.sessionId}`,
        data: { kind: 'remote-support' },
      }, () => window.focus());
    }
  }, []);

  // ── 소켓 이벤트 ──

  useEffect(() => {
    return socketService.subscribeRemoteSupport((event, payload) => {
      const sid = payload?.sessionId;
      if (event === 'rs:__connect') {
        // 끊긴 사이 서버가 수락 대기 중이던 요청도 정리했다(이 기기가 마지막 후보였다면)
        if (askRef.current) closeAskCard();
        // 담당자 소켓이 끊기면 서버가 세션을 끝낸다
        if (sessionRef.current) finishRef.current({ message: DISCONNECTED_MESSAGE });
        return;
      }
      if (event === 'rs:ask:incoming') {
        // 이미 원격지원 중이거나 다른 요청 카드가 떠 있으면 무시한다(서버가 TARGET_BUSY로 먼저 막는다)
        if (!sid || phaseRef.current !== 'idle' || askRef.current) return;
        const req = payload?.requester || {};
        const card: AskCard = {
          sessionId: sid,
          requester: { name: req.name || '직원', avatar: req.avatar ?? null, depart: req.depart ?? null },
          expiresAt: Date.now() + (Number(payload?.expiresInMs) || 60000),
        };
        askRef.current = card;
        setAskCard(card);
        setNow(Date.now());
        notifyAsk(card);
        return;
      }
      if (event === 'rs:ask:handled') {
        const card = askRef.current;
        if (!card || card.sessionId !== sid) return;
        closeAskCard();
        void flashTaskbar(true);
        // other_device(다른 기기에서 수락)·declined(내가 거절)는 조용히 닫는다
        const toast = ASK_HANDLED_TOAST[payload?.reason];
        if (toast) showToast({ type: 'info', message: toast(card.requester.name) });
        return;
      }
      if (!sid || sid !== sessionRef.current) return;
      switch (event) {
        case 'rs:accepted':
          phaseRef.current = 'picking';
          setPhase('picking');
          return;
        case 'rs:started':
          setCode(payload.code ?? null);
          setControlEnabled(!!payload.controlEnabled);
          setStartedAt(Date.now());
          phaseRef.current = 'viewing';
          setPhase('viewing');
          return;
        case 'rs:signal':
          if (viewerReadyRef.current && linkRef.current) {
            linkRef.current.post({ type: 'signal', data: payload.data });
            signalsDeliveredRef.current = true;
          } else {
            pendingSignalsRef.current.push(payload.data);
          }
          return;
        case 'rs:info':
          setPcInfo(payload.payload ?? null);
          return;
        case 'rs:status':
          setSecureDesktop(!!payload.payload?.secureDesktop);
          return;
        case 'rs:control:granted':
          setControl('granted');
          notify('success', '제어를 시작합니다. 화면을 클릭하면 키보드도 전달됩니다.');
          return;
        case 'rs:control:denied':
          setControl('none');
          notify('info', CONTROL_DENIED_TOAST[payload.reason] || '제어 요청이 받아들여지지 않았습니다.');
          return;
        case 'rs:control:revoked':
          setControl('none');
          if (payload.by !== 'operator') {
            notify('info', CONTROL_REVOKED_TOAST[payload.reason] || '제어가 끝났습니다.');
          }
          return;
        case 'rs:ended': {
          const wasViewing = !!payload.code;
          const hostName = targetRef.current?.name || payload.hostName || '';
          if (wasViewing) {
            finishRef.current({
              summary: {
                code: payload.code,
                hostName,
                durationSeconds: payload.durationSeconds ?? null,
                controlSeconds: payload.controlSeconds ?? 0,
                reason: payload.reason,
                endedBy: payload.endedBy ?? null,
                filesToHost: payload.filesToHost ?? 0,
                filesToOperator: payload.filesToOperator ?? 0,
              },
            });
          } else if (payload.reason !== 'canceled') {
            finishRef.current({ message: ENDED_TOAST[payload.reason] || '원격지원 요청이 끝났습니다.' });
          } else {
            finishRef.current({});
          }
          return;
        }
        default:
      }
    });
  }, [closeAskCard, notify, notifyAsk, showToast]);

  // ── 창 생존 ──

  useEffect(() => {
    if (isTauri()) {
      // 웹뷰 새로고침·업데이트 재시작이 남긴 뷰어 창을 걷는다(RemoteSupportHost의 rs_session_end 1회와 같은 관례)
      closeStaleViewerWindow();
      const offClose = listenNative('rs-viewer-close-requested', () => onCloseRequestedRef.current());
      const offGone = listenNative('rs-viewer-closed', () => onViewerGoneRef.current());
      return () => {
        offClose();
        offGone();
      };
    }
    // 웹 팝업은 닫혀도 알려 주지 않는 경우가 있다(pagehide 유실) — 1초마다 확인한다
    const t = window.setInterval(() => {
      if (handleRef.current?.isClosed()) onViewerGoneRef.current();
    }, 1000);
    return () => window.clearInterval(t);
  }, []);

  // 채팅 앱이 사라진다(새로고침·탭 닫기) — 창에 알린다(창이 '연결이 끊겼다'를 띄운다)
  useEffect(() => {
    const onHide = () => linkRef.current?.post({ type: 'bye' });
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // 언마운트(로그아웃) — 진행 중이면 끝내고 창도 닫는다. 수락 카드는 거절하지 않는다 — 같은 계정의 다른 기기가 아직
  // 받을 수 있고, 이 기기의 소켓이 끊기면 서버가 후보에서 뺀다(마지막 후보였다면 직원에게 disconnect로 알린다)
  useEffect(
    () => () => {
      if (sessionRef.current) socketService.emitRemoteSupport('rs:end', { sessionId: sessionRef.current });
      linkRef.current?.post({ type: 'bye' });
      const h = handleRef.current;
      linkRef.current?.close();
      linkRef.current = null;
      handleRef.current = null;
      h?.close();
    },
    []
  );

  // ── 채팅 앱 렌더(수락 카드 · 상태 카드 · 창이 없을 때의 요약) ──

  const saveNote = async () => {
    if (!summary?.code) return;
    setSavingNote(true);
    try {
      await remoteSupportApi.saveNote(summary.code, note);
      showToast({ type: 'success', message: '처리 메모를 저장했습니다.' });
      setSummary(null);
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '메모를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setSavingNote(false);
    }
  };

  const summaryModal = (
    <Modal
      isOpen={!!summary}
      onClose={() => setSummary(null)}
      title="원격지원을 마쳤습니다"
      size="sm"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSummary(null)}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            닫기
          </button>
          <button
            type="button"
            onClick={saveNote}
            disabled={savingNote || !note.trim()}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
          >
            메모 저장
          </button>
        </div>
      }
    >
      {summary && (
        <div className="space-y-3 text-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-slate-500">직원</dt>
            <dd className="text-slate-800">{summary.hostName}</dd>
            <dt className="text-slate-500">총 시간</dt>
            <dd className="text-slate-800 tabular-nums">
              {formatDuration(summary.durationSeconds)}
              {summary.controlSeconds > 0 && ` (제어 ${formatDuration(summary.controlSeconds)})`}
            </dd>
            <dt className="text-slate-500">세션 번호</dt>
            <dd className="text-slate-800 tabular-nums">{summary.code}</dd>
            {filesLabel(summary.filesToHost, summary.filesToOperator, 'operator') && (
              <>
                <dt className="text-slate-500">파일</dt>
                <dd className="text-slate-800">{filesLabel(summary.filesToHost, summary.filesToOperator, 'operator')}</dd>
              </>
            )}
            <dt className="text-slate-500">종료</dt>
            <dd className="text-slate-800">{endReasonLabel(summary.reason)}</dd>
          </dl>
          <label className="block">
            <span className="block mb-1 text-xs font-medium text-slate-600">처리 메모 (직원도 볼 수 있습니다)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
              rows={4}
              placeholder="무엇을 확인하고 어떻게 해결했는지 적어 주세요"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
            />
          </label>
        </div>
      )}
    </Modal>
  );

  const askRemaining = askCard ? Math.max(0, Math.ceil((askCard.expiresAt - now) / 1000)) : 0;

  // 직원이 보낸 원격지원 요청 — 수신 통화 배너와 같은 자리(제목 표시줄 아래 우상단). 일하던 화면을 막지 않는다
  const askCardView = askCard ? (
    <div
      role="alertdialog"
      aria-labelledby="rs-ask-title"
      className="fixed top-[calc(var(--titlebar-h,0px)+1rem)] right-4 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-2xl bg-white border border-slate-200 shadow-xl p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <p id="rs-ask-title" className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <LifeBuoy size={16} className="text-slate-500 shrink-0" aria-hidden="true" />
          원격지원 요청
        </p>
        <span className="text-xs text-slate-500 tabular-nums">{askRemaining}초</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <img
          src={resolveAvatarUrl(askCard.requester.avatar)}
          onError={handleAvatarError}
          alt=""
          className="w-11 h-11 rounded-full object-cover bg-slate-100 shrink-0"
        />
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 break-anywhere">{askCard.requester.name}</p>
          {askCard.requester.depart && <p className="text-xs text-slate-500 break-anywhere">{askCard.requester.depart}</p>}
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
        PC 화면을 봐 달라고 요청했습니다. 수락하면 원격지원 창이 열리고, 직원이 확인하면 직원 PC의 모든 모니터 화면이 보입니다.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={declineAsk}
          className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
        >
          거절
        </button>
        <button
          type="button"
          onClick={acceptAsk}
          className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white transition-colors"
        >
          수락
        </button>
      </div>
    </div>
  ) : null;

  if (phase === 'idle') {
    return (
      <>
        {askCardView}
        {summaryModal}
      </>
    );
  }

  const elapsed = startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;
  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;
  const granted = control === 'granted';
  const statusText =
    phase === 'viewing'
      ? `${granted ? '제어 중' : '보는 중'} · ${mmss}`
      : phase === 'picking'
        ? '직원이 화면 공유를 확인하는 중'
        : '연결 대기 중';

  return (
    <>
      {/* 채팅 입력창(전송 버튼)을 가리지 않게 우하단에서 한 칸 위 */}
      <div
        role="status"
        className="fixed bottom-24 right-6 z-50 flex items-center gap-3 p-3 bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700"
      >
        <span
          className={`w-2.5 h-2.5 rounded-full shrink-0 ${
            phase === 'viewing' ? (granted ? 'bg-brand-500' : 'bg-orange-500') : 'bg-slate-500'
          }`}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-xs font-medium truncate max-w-[12rem]">{target?.name}님 PC 원격지원</p>
          <p className="text-xs text-slate-400 tabular-nums">{statusText}</p>
        </div>
        <button
          type="button"
          onClick={() => handleRef.current?.focus()}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-white/10 text-xs font-medium"
        >
          <AppWindow size={14} aria-hidden="true" />
          창 보기
        </button>
        <button
          type="button"
          onClick={() => (phase === 'viewing' ? setConfirmEnd(true) : endSession())}
          className="px-2.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-xs font-medium"
        >
          종료
        </button>
      </div>
      <ConfirmDialog
        isOpen={confirmEnd}
        title="원격지원 종료"
        message={`${target?.name}님 PC의 원격지원을 끝낼까요?`}
        confirmLabel="종료"
        danger
        onConfirm={() => {
          setConfirmEnd(false);
          endSession();
          handleRef.current?.focus(); // 요약·메모는 원격 창에 뜬다
        }}
        onCancel={() => setConfirmEnd(false)}
      />
      {summaryModal}
    </>
  );
};
